import { track } from '@betterment/js-runtime';
import { Box, Button, DropdownMenu, ShimmerText } from '@betterment/samba-react';

import { LegacyTransferType, LegalAccountTaxationType } from '../../../graphql/schema';
import { locationAssign } from '../../path-helpers';
import { urlBuilder } from '../../urlBuilder';

export type TransactionButtonsTransactionOption =
  | { __typename: 'CharitableDonationOption'; id: string }
  | { __typename: 'RolloverOption'; id: string }
  | { __typename: 'RothIraConversionOption'; id: string }
  | { __typename: 'TransferOption'; id: string; legacyTransferType: LegacyTransferType }
  | { __typename: 'DeferralSettingsEditOption' | 'WireTransferOption' | 'ExternalTransferOption'; id: string };

export type TransactionOptionsProps = {
  transactionOptions: TransactionButtonsTransactionOption[];
  subAccounts: {
    id: number;
    taxationType: LegalAccountTaxationType;
  }[];
  isCompact?: boolean;
};

const LoadingItems = () => (
  <>
    <DropdownMenu.Item css={{ paddingY: 'space.100', width: '300px' }}>
      <ShimmerText textStyle="text50" />
    </DropdownMenu.Item>
    <DropdownMenu.Item css={{ paddingY: 'space.100', width: '300px' }}>
      <ShimmerText textStyle="text50" />
    </DropdownMenu.Item>
  </>
);
export const DepositButton = ({ transactionOptions, subAccounts, isCompact = false }: TransactionOptionsProps) => {
  if (
    !transactionOptions.some(
      o => o.__typename === 'TransferOption' && o.legacyTransferType === LegacyTransferType.Deposit
    )
  ) {
    return null;
  }

  const onDepositClick = () => {
    if (subAccounts.length === 1) {
      locationAssign(urlBuilder.appDepositSetupDestination.absolute({ subAccountId: subAccounts[0].id }));
    } else {
      locationAssign(urlBuilder.appDepositSetup.absolute());
    }
  };

  return (
    <Button
      css={{ width: { base: '100%', md: 'auto' } }}
      onClick={onDepositClick}
      spacing={isCompact ? 'compact' : 'default'}
    >
      Deposit
    </Button>
  );
};

export const OtherTransactionOptions: React.FC<TransactionOptionsProps> = ({ transactionOptions, subAccounts }) => {
  const items = transactionOptions
    .map((option, index) => {
      const key = `${index}:${option.__typename}`;
      switch (option.__typename) {
        case 'RolloverOption': {
          const url = urlBuilder.getRollovers.absolute();
          return { key, eventName: 'Rollover', url, text: 'Rollover to Betterment' };
        }
        case 'RothIraConversionOption': {
          const subAccountId = subAccounts.find(a => a.taxationType === LegalAccountTaxationType.TraditionalIra)?.id;
          if (!subAccountId) return null;
          const url = urlBuilder.appConvertFundsToRoth.absolute({ subAccountId });
          return { key, eventName: 'ConvertToRoth', url, text: 'Convert funds to Roth' };
        }
        case 'CharitableDonationOption':
          return {
            key,
            eventName: 'Charity',
            url: urlBuilder.appCharitableGiftStart.absolute(),
            text: 'Donate to charity',
          };
        case 'TransferOption': {
          const subAccountId = subAccounts.length === 1 ? subAccounts[0].id : undefined;
          switch (option.legacyTransferType) {
            case LegacyTransferType.Transfer: {
              const url = subAccountId
                ? urlBuilder.showSubAccountTransferSetup.absolute({ subAccountId })
                : urlBuilder.showTransferSetup.absolute();
              return { key, eventName: 'GoalTransfer', url, text: 'Transfer to a Betterment account' };
            }
            case LegacyTransferType.Withdrawal: {
              const url = subAccountId
                ? urlBuilder.appWithdrawalSetup.absolute({ subAccountId })
                : urlBuilder.appWithdrawalSetup.absolute();
              return { key, eventName: 'Withdraw', url, text: 'Withdraw to a bank' };
            }
            default:
              return null;
          }
        }
        default:
          return null;
      }
    })
    .filter(i => i !== null);

  if (items.length === 0) return null;

  return (
    <>
      {items ? (
        items.map(({ key, eventName, url, text }) => (
          <Box key={key}>
            <DropdownMenu.Item
              onClick={() => {
                track('ElementClicked', { module: 'DropdownMenu', name: eventName });
                locationAssign(url);
              }}
            >
              {text}
            </DropdownMenu.Item>
          </Box>
        ))
      ) : (
        <LoadingItems />
      )}
      {/* Temporarily commenting this out I believe this will be added later */}
      {/* <DropdownMenu.Separator css={{ my: 'space.100' }} />
      <DropdownMenu.Item
        onClick={() => {
          track('ElementClicked', { module: 'DropdownMenu', name: 'TransferCenter' });
          locationAssign(urlBuilder.getPendingTransfers.absolute());
        }}
      >
        View transfers
      </DropdownMenu.Item> */}
    </>
  );
};

export const OtherTransactionOptionsMenu = ({
  transactionOptions,
  subAccounts,
  isCompact = false,
}: TransactionOptionsProps) => {
  const rolloverOption = transactionOptions.find(o => o.__typename === 'RolloverOption');

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger asChild={true}>
        <Button
          variant="solidSecondary"
          css={{ textWrap: 'nowrap', width: { base: '100%', md: 'auto' } }}
          spacing={isCompact ? 'compact' : 'default'}
        >
          {rolloverOption && !isCompact ? 'Transfer or rollover' : 'Transfer'}
          <Button.Icon name="CaretDown" />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end">
        <OtherTransactionOptions transactionOptions={transactionOptions} subAccounts={subAccounts} />
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};
